import { Injectable } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class DynamicIconService {
  private iconMap = new Map<string, string>();
  private currentIcon$ = new BehaviorSubject<string>('bi bi-ui-checks-grid');

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute
  ) {
    this.initializeDynamicIcons();
  }

  private initializeDynamicIcons(): void {
    const routePatterns = {
      'dashboard': 'bi bi-ui-checks-grid',
      'clients|customers': 'bi bi-list-task',
      'requests': 'bi bi-list-task',
      'proposals': 'bi bi-list-task',
      'projects': 'bi bi-list-task',
      'users': 'bi bi-list-task',
      'categories': 'bi bi-list-task',
      'companies': 'bi bi-list-task',
      'jurisdictions': 'bi bi-list-task',
      'item-types': 'bi bi-list-task',
      'item-products': 'bi bi-list-task',
      'laws': 'bi bi-list-task',
      'regulations': 'bi bi-list-task',
      'items': 'bi bi-list-task',
      'file-manager': 'bi bi-folder2-open',
      'activity-planning': 'bi bi-calendar-date',
      'project-payments-report': 'bi bi-calendar-check',
      'regulators': 'bi bi-list-task',
      'campaigns': 'bi bi-list-task',
      'employees|specialisations': 'bi bi-list-task',
    };

    Object.entries(routePatterns).forEach(([pattern, icon]) => {
      const routes = pattern.split('|');
      routes.forEach(route => {
        this.iconMap.set(route.trim(), icon);
      });
    });
  }

  public getIconForSection(section: string | null, subsection: string | null = null): string {
    if (!section) {
      return 'bi bi-ui-checks-grid';
    }

    if (subsection) {
      return 'bi bi-tools';
    }

    const normalizedSection = section.toLowerCase().replace(/\s+/g, '-');

    const directMatch = this.iconMap.get(normalizedSection);
    if (directMatch) {
      return directMatch;
    }

    for (const [key, icon] of this.iconMap.entries()) {
      if (normalizedSection.includes(key) || key.includes(normalizedSection)) {
        return icon;
      }
    }

    return 'bi bi-ui-checks-grid';
  }

  public registerIcon(section: string, icon: string): void {
    const normalizedSection = section.toLowerCase().replace(/\s+/g, '-');
    this.iconMap.set(normalizedSection, icon);
  }

  public registerMultipleIcons(icons: Record<string, string>): void {
    Object.entries(icons).forEach(([section, icon]) => {
      this.registerIcon(section, icon);
    });
  }

  public setCurrentIcon(icon: string): void {
    this.currentIcon$.next(icon);
  }

  public getCurrentIcon$(): Observable<string> {
    return this.currentIcon$.asObservable();
  }

  public getIconMap(): Map<string, string> {
    return new Map(this.iconMap);
  }

  public unregisterIcon(section: string): void {
    const normalizedSection = section.toLowerCase().replace(/\s+/g, '-');
    this.iconMap.delete(normalizedSection);
  }
}
